/**
 * Alarm list filter model.
 *
 * The filter bar, the saved views and the URL all speak this one shape, and
 * `filtersToQuery` is the single place that turns it into the documented query
 * string (`state`, `severity`, `integration_id`, `team_id`, `q`, `from`, `to`,
 * plus `filter=<url-encoded DSL JSON>`; docs/06 section 4).
 */
import type { Condition, FieldCondition } from '@vam/schema';
import type { AlarmListQuery } from '../../lib/api.js';
import { isEmptyCondition } from '../../lib/dsl.js';
import type { SavedView } from '../../lib/schemas.js';

export const GROUP_BY_OPTIONS = [
  { value: '', label: 'No grouping' },
  { value: 'severity', label: 'Severity' },
  { value: 'integration', label: 'Integration' },
  { value: 'category', label: 'Category' },
  { value: 'entity', label: 'Entity' },
  { value: 'team', label: 'Team' },
] as const;
export type GroupBy = (typeof GROUP_BY_OPTIONS)[number]['value'];

export const SORT_OPTIONS = [
  { value: 'last_seen:desc', label: 'Newest activity' },
  { value: 'last_seen:asc', label: 'Oldest activity' },
  { value: 'first_seen:desc', label: 'Newest alarm' },
  { value: 'severity:desc', label: 'Most severe' },
  { value: 'occurrence_count:desc', label: 'Most occurrences' },
] as const;
export type Sort = (typeof SORT_OPTIONS)[number]['value'];

export const RANGE_OPTIONS = [
  { value: '1h', label: 'Last hour', hours: 1 },
  { value: '24h', label: 'Last 24 hours', hours: 24 },
  { value: '7d', label: 'Last 7 days', hours: 24 * 7 },
  { value: '30d', label: 'Last 30 days', hours: 24 * 30 },
  { value: 'all', label: 'All time', hours: 0 },
] as const;
export type TimeRange = (typeof RANGE_OPTIONS)[number]['value'];

export interface AlarmFilters {
  state: string;
  severity: string;
  integration_id: string;
  team_id: string;
  assignee_id: string;
  tag: string;
  q: string;
  range: TimeRange;
  sort: Sort;
  group_by: GroupBy;
  /** Advanced condition DSL, edited in the JSON editor. */
  dsl: Condition | null;
}

export function defaultFilters(): AlarmFilters {
  return {
    state: 'active',
    severity: '',
    integration_id: '',
    team_id: '',
    assignee_id: '',
    tag: '',
    q: '',
    range: 'all',
    sort: 'last_seen:desc',
    group_by: '',
    dsl: null,
  };
}

/** How many *narrowing* filters are on, for the "clear" affordance. */
export function activeFilterCount(filters: AlarmFilters): number {
  const base = defaultFilters();
  let count = 0;
  for (const key of [
    'state',
    'severity',
    'integration_id',
    'team_id',
    'assignee_id',
    'tag',
    'q',
    'range',
  ] as const) {
    if (filters[key] !== base[key]) count += 1;
  }
  if (filters.dsl && !isEmptyCondition(filters.dsl)) count += 1;
  return count;
}

/** ISO timestamp for the start of the selected range, or undefined. */
export function rangeStart(range: TimeRange, now = Date.now()): string | undefined {
  const option = RANGE_OPTIONS.find((r) => r.value === range);
  if (!option || option.hours === 0) return undefined;
  return new Date(now - option.hours * 3_600_000).toISOString();
}

/** Turn the filter model into API query parameters. */
export function filtersToQuery(
  filters: AlarmFilters,
  extra: { limit?: number; cursor?: string | null } = {},
  now = Date.now(),
): AlarmListQuery {
  const query: AlarmListQuery = {};
  if (filters.state) query.state = filters.state;
  if (filters.severity) query.severity = filters.severity;
  if (filters.integration_id) query.integration_id = filters.integration_id;
  if (filters.team_id) query.team_id = filters.team_id;
  if (filters.assignee_id) query.assignee_id = filters.assignee_id;
  if (filters.tag) query.tag = filters.tag;
  if (filters.q) query.q = filters.q;
  const from = rangeStart(filters.range, now);
  if (from) query.since = from;
  if (filters.sort) query.sort = filters.sort;
  if (filters.group_by) query.group_by = filters.group_by;
  if (filters.dsl && !isEmptyCondition(filters.dsl)) {
    // Plain JSON: the URL builder encodes query values once, which is the one
    // decoding the server undoes. Encoding here too was double encoding.
    query.filter = JSON.stringify(filters.dsl);
  }
  if (extra.limit !== undefined) query.limit = extra.limit;
  if (extra.cursor) query.cursor = extra.cursor;
  return query;
}

/** Filters -> URL search params, so a filtered list is linkable. */
export function filtersToSearchParams(filters: AlarmFilters): URLSearchParams {
  const base = defaultFilters();
  const params = new URLSearchParams();
  for (const key of [
    'state',
    'severity',
    'integration_id',
    'team_id',
    'assignee_id',
    'tag',
    'q',
    'range',
    'sort',
    'group_by',
  ] as const) {
    if (filters[key] && filters[key] !== base[key]) params.set(key, String(filters[key]));
  }
  if (filters.dsl && !isEmptyCondition(filters.dsl)) {
    params.set('filter', JSON.stringify(filters.dsl));
  }
  return params;
}

/** URL search params -> filters, ignoring anything unrecognised. */
export function filtersFromSearchParams(params: URLSearchParams): AlarmFilters {
  const filters = defaultFilters();
  const read = (key: string): string => params.get(key) ?? '';
  if (params.has('state')) filters.state = read('state');
  filters.severity = read('severity');
  filters.integration_id = read('integration_id');
  filters.team_id = read('team_id');
  filters.assignee_id = read('assignee_id');
  filters.tag = read('tag');
  filters.q = read('q');
  const range = read('range');
  if (RANGE_OPTIONS.some((r) => r.value === range)) filters.range = range as TimeRange;
  const sort = read('sort');
  if (SORT_OPTIONS.some((s) => s.value === sort)) filters.sort = sort as Sort;
  const groupBy = read('group_by');
  if (GROUP_BY_OPTIONS.some((g) => g.value === groupBy)) filters.group_by = groupBy as GroupBy;
  const raw = params.get('filter');
  if (raw) {
    try {
      filters.dsl = JSON.parse(raw) as Condition;
    } catch {
      filters.dsl = null;
    }
  }
  // An old link may still carry the 'none' the filter bar once offered; the
  // API has no unassigned filter and rejects it as a malformed uuid.
  if (filters.assignee_id === 'none') filters.assignee_id = '';
  return filters;
}

// ---------------------------------------------------------------------------
// Saved views
//
// The API stores a view's filter as a condition-DSL tree (`ConditionSchema`),
// its sort as `{ field, direction }` and its group_by from its own enum
// (apps/api/src/views/views.dto.ts). The console's filter model is encoded
// into exactly that and decoded back; anything in a stored condition the
// simple filters cannot express rides along as the advanced `dsl`.
// ---------------------------------------------------------------------------

/** What `state=active` means to the list API. */
const ACTIVE_STATES = ['open', 'acknowledged', 'snoozed'] as const;

/** Console grouping -> the view enum (`entity` is `entity_name` there). */
const VIEW_GROUP_BY: Record<Exclude<GroupBy, ''>, string> = {
  severity: 'severity',
  integration: 'integration',
  category: 'category',
  entity: 'entity_name',
  team: 'team',
};

function csv(value: string): string[] {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

function isLeaf(condition: Condition): condition is FieldCondition {
  return typeof (condition as FieldCondition).field === 'string';
}

/** The `q` box searches title, message and entity name, case-insensitively. */
function searchCondition(q: string): Condition {
  return {
    any: ['title', 'message', 'entity.name'].map((field) => ({
      field,
      op: 'contains' as const,
      value: q,
      ci: true,
    })),
  };
}

/** The value of `searchCondition` when `condition` is one, else undefined. */
function searchOf(condition: Condition): string | undefined {
  if (!('any' in condition) || condition.any.length !== 3) return undefined;
  const leaves = condition.any;
  if (!leaves.every(isLeaf)) return undefined;
  const fields = leaves.map((leaf) => leaf.field).sort();
  if (fields.join() !== ['entity.name', 'message', 'title'].join()) return undefined;
  const first = leaves[0]!;
  if (first.op !== 'contains' || typeof first.value !== 'string') return undefined;
  const value = first.value;
  return leaves.every((leaf) => leaf.op === 'contains' && leaf.value === value && leaf.ci === true)
    ? value
    : undefined;
}

/** The simple filters (and the advanced DSL) as one condition the API can store and evaluate. */
export function filtersToCondition(filters: AlarmFilters): Condition {
  const all: Condition[] = [];
  if (filters.state) {
    all.push({
      field: 'state',
      op: 'in',
      value: filters.state === 'active' ? [...ACTIVE_STATES] : csv(filters.state),
    });
  }
  if (filters.severity) all.push({ field: 'severity', op: 'in', value: csv(filters.severity) });
  if (filters.integration_id) {
    all.push({ field: 'integration_id', op: 'eq', value: filters.integration_id });
  }
  if (filters.team_id) all.push({ field: 'team', op: 'eq', value: filters.team_id });
  if (filters.assignee_id) all.push({ field: 'assignee_id', op: 'eq', value: filters.assignee_id });
  if (filters.tag) all.push({ field: 'tags', op: 'eq', value: filters.tag });
  if (filters.q) all.push(searchCondition(filters.q));
  const rangeMinutes = rangeToMinutes(filters.range);
  if (rangeMinutes !== undefined)
    all.push({ field: 'idle_minutes', op: 'lt', value: rangeMinutes });
  if (filters.dsl && !isEmptyCondition(filters.dsl)) all.push(filters.dsl);
  return { all };
}

/**
 * A relative range as the DSL says it: `idle_minutes lt N` is "last seen within
 * N minutes", exactly the `since` bound the live list sends.
 */
function rangeToMinutes(range: TimeRange): number | undefined {
  const option = RANGE_OPTIONS.find((r) => r.value === range);
  return option && option.hours > 0 ? option.hours * 60 : undefined;
}

function rangeFromMinutes(minutes: unknown): TimeRange | undefined {
  if (typeof minutes !== 'number') return undefined;
  return RANGE_OPTIONS.find((r) => r.hours > 0 && r.hours * 60 === minutes)?.value;
}

/**
 * A stored condition back into the filter model. Leaves this console wrote are
 * lifted into the simple filters; whatever else is there (a view written
 * through the API, a hand-edited condition) becomes the advanced `dsl`, so
 * nothing the view says is dropped.
 */
export function filtersFromCondition(condition: unknown): AlarmFilters {
  const filters = { ...defaultFilters(), state: '' };
  if (typeof condition !== 'object' || condition === null) return filters;
  const root = condition as Condition;
  const members: Condition[] = 'all' in root && Array.isArray(root.all) ? root.all : [root];
  const rest: Condition[] = [];
  for (const member of members) {
    if (isLeaf(member)) {
      const list =
        Array.isArray(member.value) && member.value.every((v) => typeof v === 'string')
          ? member.value
          : typeof member.value === 'string'
            ? [member.value]
            : null;
      if (member.field === 'state' && (member.op === 'in' || member.op === 'eq') && list) {
        const sorted = [...list].sort().join();
        filters.state = sorted === [...ACTIVE_STATES].sort().join() ? 'active' : list.join(',');
        continue;
      }
      if (member.field === 'severity' && (member.op === 'in' || member.op === 'eq') && list) {
        filters.severity = list.join(',');
        continue;
      }
      if (member.field === 'integration_id' && member.op === 'eq' && list?.length === 1) {
        filters.integration_id = list[0]!;
        continue;
      }
      if (member.field === 'team' && member.op === 'eq' && list?.length === 1) {
        filters.team_id = list[0]!;
        continue;
      }
      if (member.field === 'assignee_id' && member.op === 'eq' && list?.length === 1) {
        filters.assignee_id = list[0]!;
        continue;
      }
      if (member.field === 'idle_minutes' && member.op === 'lt') {
        const range = rangeFromMinutes(member.value);
        if (range) {
          filters.range = range;
          continue;
        }
      }
      if (member.field === 'tags' && member.op === 'eq' && list?.length === 1) {
        filters.tag = list[0]!;
        continue;
      }
    } else {
      const q = searchOf(member);
      if (q !== undefined) {
        filters.q = q;
        continue;
      }
    }
    rest.push(member);
  }
  filters.dsl = rest.length === 0 ? null : rest.length === 1 ? rest[0]! : { all: rest };
  return filters;
}

/** `last_seen:desc` -> the view's `{ field, direction }`. */
export function sortToView(sort: Sort): { field: string; direction: 'asc' | 'desc' } {
  const [field, direction] = sort.split(':');
  return { field: field ?? 'last_seen', direction: direction === 'asc' ? 'asc' : 'desc' };
}

/** The body `POST /views` takes for the current filters. */
export function savedViewPayload(
  filters: AlarmFilters,
  view: { name: string; shared: boolean; teamId: string | null },
): Record<string, unknown> {
  return {
    name: view.name,
    shared: view.shared,
    ...(view.teamId ? { team_id: view.teamId } : {}),
    filter: filtersToCondition(filters),
    group_by: filters.group_by ? VIEW_GROUP_BY[filters.group_by] : null,
    sort: sortToView(filters.sort),
  };
}

/** A view as the filter model: its condition, sort and grouping. */
export function filtersFromSavedView(
  view: Pick<SavedView, 'filter' | 'sort' | 'group_by'>,
): AlarmFilters {
  const filters = filtersFromCondition(view.filter);
  const sort = view.sort as { field?: unknown; direction?: unknown } | null;
  if (sort && typeof sort.field === 'string') {
    const candidate = `${sort.field}:${sort.direction === 'asc' ? 'asc' : 'desc'}`;
    if (SORT_OPTIONS.some((s) => s.value === candidate)) filters.sort = candidate as Sort;
  }
  if (view.group_by) {
    const consoleKey = (Object.entries(VIEW_GROUP_BY).find(([, v]) => v === view.group_by) ??
      [])[0];
    if (consoleKey) filters.group_by = consoleKey as GroupBy;
  }
  return filters;
}
